import { useEffect } from "react";
import type { LucideIcon } from "lucide-react";
import { cn } from "@/lib/utils";

export type ToolbarAction = {
  label: string;
  icon: LucideIcon;
  onClick: () => void;
  /** Phím tắt hệ cũ: "1" = Alt+1 (Thêm mới), "2" = Ghi lại, "3" = Đóng. */
  altKey?: "1" | "2" | "3" | "4";
  disabled?: boolean;
  tone?: "default" | "primary" | "danger";
};

/**
 * Dãy nút hành động ở góc phải màn, kèm phím tắt Alt+1/2/3 giống hệ cũ — người
 * dùng cũ thao tác chủ yếu bằng phím tắt nên tooltip cũng ghi rõ như bên đó
 * ("Thêm mới Alt+1", "Ghi lại Alt+2", "Đóng Alt+3").
 */
export function ScreenToolbar({ actions }: { actions: ToolbarAction[] }) {
  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if (!e.altKey) return;
      const hit = actions.find((a) => a.altKey && a.altKey === e.key);
      if (!hit || hit.disabled) return;
      e.preventDefault();
      hit.onClick();
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [actions]);

  return (
    <div className="flex items-center gap-1.5">
      {actions.map((a) => (
        <button
          key={a.label}
          type="button"
          disabled={a.disabled}
          onClick={a.onClick}
          title={a.altKey ? `${a.label} Alt+${a.altKey}` : a.label}
          className={cn(
            "inline-flex items-center gap-1.5 h-8 px-3 rounded-sm border text-xs font-medium transition-colors",
            "disabled:opacity-40 disabled:pointer-events-none",
            a.tone === "primary"
              ? "border-transparent bg-primary text-primary-foreground hover:bg-primary/90"
              : a.tone === "danger"
                ? "border-border text-destructive hover:bg-destructive/10"
                : "border-border bg-transparent hover:bg-muted",
          )}
        >
          <a.icon className="h-3.5 w-3.5" />
          {a.label}
        </button>
      ))}
    </div>
  );
}
